import { Button, Modal, Spinner } from 'react-bootstrap'
import { useMutation } from '@apollo/client'
import { useEffect } from 'react'
import { toast } from 'react-toastify'
import { DELETE_RESOURCE_CATEGORY } from '../../../gql/mutations/resourceCategoryMutations'
import { IDeleteResourceCategoryResponse, IResourceCategory } from '../../../types/resourceCategory'
import { parseId } from '../../../helpers'

interface IModalProps {
  isOpen: boolean
  onClose: () => void
  refetch: () => void
  category: IResourceCategory
}

export const ModalDeleteCategory: React.FC<IModalProps> = ({
  isOpen,
  onClose,
  refetch,
  category,
}) => {
  const [deleteCategory, { data, loading, error }] =
    useMutation<IDeleteResourceCategoryResponse>(DELETE_RESOURCE_CATEGORY)

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  useEffect(() => {
    if (!data) return
    if (data.deleteResourceCategory.success) {
      onClose()
      refetch()
      toast.success(data.deleteResourceCategory.message || 'Folder deleted successfully')
    } else if (
      data.deleteResourceCategory.errors &&
      data.deleteResourceCategory.errors.length > 0
    ) {
      toast.error(`Error: ${data.deleteResourceCategory.errors[0].message}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  const handleDelete = async () => {
    try {
      await deleteCategory({ variables: { id: parseId(category.id) } })
    } catch (err) {
      console.error('Error deleting category:', err)
      toast.error('Error deleting folder')
    }
  }

  return (
    <Modal show={isOpen} onHide={onClose} centered backdrop="static">
      <Modal.Header closeButton className="bg-danger text-white">
        <Modal.Title className="d-flex align-items-center gap-2">
          <i className="bi bi-exclamation-triangle-fill" /> Delete Folder
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="mb-3">
          Are you sure you want to delete the folder{' '}
          <strong>&quot;{category.name}&quot;</strong>?
        </p>

        {category.childrenCount > 0 && (
          <div className="alert alert-warning mb-3">
            <i className="bi bi-exclamation-triangle me-2" />
            This folder has <strong>{category.childrenCount}</strong> subfolder(s). You must move
            or delete them first.
          </div>
        )}

        <div className="p-3 border rounded bg-light small">
          <i className="bi bi-info-circle me-2 text-primary" />
          Resources inside this folder will <strong>not</strong> be deleted — they will be moved
          to &quot;Uncategorized&quot;.
          {category.resourcesCount > 0 && (
            <div className="mt-1">
              This folder currently contains <strong>{category.resourcesCount}</strong>{' '}
              resource(s).
            </div>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleDelete} disabled={loading || category.childrenCount > 0}>
          {loading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Deleting...
            </>
          ) : (
            <>
              <i className="bi bi-trash me-1" /> Yes, Delete Folder
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
